import React from "react";
import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    HStack,
    SimpleGrid,
    Button,
    Icon,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import {
    FaUserGraduate,
    FaClipboardList,
    FaMoneyBillWave,
    FaUserShield,
    FaSchool,
    FaChartLine,
    FaBell,
    FaFileAlt,
    FaCheckCircle,
    FaArrowRight,
} from "react-icons/fa";
import PublicNavbar from "../Landing/PublicNavbar";
import PublicFooter from "../Landing/PublicFooter";
import {
    DoodleUnderline,
    DoodleSparkle,
    WashiTape,
    DoodlePaperclip,
} from "../../CustomUi/DoodleIcons";
import { APP_REGISTER_URL } from "../../../services/constants";

const features = [
    {
        icon: FaUserGraduate,
        title: "Student Management",
        badge: "Core Feature",
        tapeColor: "#FEF08A",
        description: "Complete student records from enrolment to graduation. Track attendance, class assignments, and academic history — all searchable and exportable.",
        highlights: ["Student profiles & enrollment", "Class and level assignments", "Bulk import via CSV/Excel", "Student promotion system"],
    },
    {
        icon: FaClipboardList,
        title: "Results & Report Cards",
        badge: "Most Loved ⭐️",
        tapeColor: "#FED7AA",
        description: "Automate result entry, calculation, and report card generation. Customise grade criteria per term and export print-ready PDFs in bulk.",
        highlights: ["Flexible grade criteria", "Automatic position calculation", "Bulk PDF report cards", "Term-by-term history"],
    },
    {
        icon: FaMoneyBillWave,
        title: "Fee Collection",
        badge: "Result Finance 💳",
        tapeColor: "#BBF7D0",
        description: "Each school gets a dedicated Result Finance virtual account. Parents pay directly, and collections are tracked in real time — no manual reconciliation.",
        highlights: ["Result Finance virtual accounts", "Real-time payment alerts", "Fee category management", "Transaction history"],
    },
    {
        icon: FaUserShield,
        title: "Parent Portal",
        badge: "Zero Calling 📞",
        tapeColor: "#BAE6FD",
        description: "Parents log in to see their child's results, payment history, and school communications. Reduces admin calls and improves trust.",
        highlights: ["Result & fee visibility", "Downloadable report cards", "Payment receipts", "School announcements"],
    },
    {
        icon: FaBell,
        title: "Notifications",
        badge: "SMS & In-App",
        tapeColor: "#DDD6FE",
        description: "Keep parents and staff in the loop with automated notifications for new results, fee payments, and important school updates.",
        highlights: ["Result release alerts", "Payment confirmations", "Custom announcements", "In-app + email delivery"],
    },
    {
        icon: FaFileAlt,
        title: "Bulk Operations",
        badge: "Fast ⚡️",
        tapeColor: "#FEF08A",
        description: "Generate and export results, report cards, and financial summaries in bulk. Everything your end-of-term operations require — fast.",
        highlights: ["Bulk result entry", "Bulk PDF generation", "Data export (CSV, Excel)", "Batch student promotion"],
    },
    {
        icon: FaSchool,
        title: "School Configuration",
        badge: "Your Rules 🏛️",
        tapeColor: "#FED7AA",
        description: "Full control over your academic structure — sessions, terms, levels, classes, subjects, and assessment types — exactly as your school runs.",
        highlights: ["Session & term setup", "Class & level structure", "Subject management", "Assessment type config"],
    },
    {
        icon: FaChartLine,
        title: "Analytics & Audit",
        badge: "Full Accountability",
        tapeColor: "#BBF7D0",
        description: "Track staff actions, analyse student performance trends, and get a clear financial overview. Full audit trail so nothing is ever unaccounted for.",
        highlights: ["Staff activity audit trail", "Performance analytics", "Financial reporting", "Role-based access control"],
    },
];

import { usePageTitle } from "../../../hooks/usePageTitle";

const FeaturesPage: React.FC = () => {
    usePageTitle(
        "Features & Capabilities",
        "Explore student management, automated report cards, fee collection, and parent communication designed for Nigerian schools."
    );
    const navigate = useNavigate();

    return (
        <Box minH="100vh" bg="#FAF8F2">
            <PublicNavbar />

            {/* Hero */}
            <Box py={[16, 24]} bg="brand.900" color="white" position="relative" overflow="hidden">
                <Container maxW="4xl">
                    <VStack gap={5} textAlign="center">
                        <HStack gap={2} justify="center">
                            <DoodleSparkle size={20} color="#FBBF24" />
                            <Box
                                px={3.5}
                                py={1}
                                bg="whiteAlpha.150"
                                borderRadius="full"
                                border="1px solid"
                                borderColor="whiteAlpha.200"
                            >
                                <Text fontSize="xs" fontWeight="bold" letterSpacing="widest" textTransform="uppercase" color="gold.300">
                                    Feature Tour
                                </Text>
                            </Box>
                            <DoodleSparkle size={20} color="#FBBF24" />
                        </HStack>

                        <Heading as="h1" fontSize={["3xl", "4xl", "5xl"]} fontWeight="extrabold" lineHeight="1.15">
                            Everything your school needs,{" "}
                            <Box as="span" position="relative" display="inline-block" color="gold.400">
                                nothing it doesn&apos;t
                                <DoodleUnderline position="absolute" bottom="-8px" left="0" color="#F59E0B" height="14px" />
                            </Box>
                        </Heading>
                        <Text fontSize={["md", "lg"]} opacity={0.8} maxW="2xl" lineHeight="1.7">
                            Result is purpose-built for Nigerian and African schools. Every feature solves a real classroom or bursary headache — no bloat, no confusion.
                        </Text>
                        <HStack gap={4} flexWrap="wrap" justify="center" pt={3}>
                            <Button
                                size="lg"
                                bg="gold.400"
                                color="#1A1208"
                                fontWeight="bold"
                                px={8}
                                borderRadius="xl"
                                border="2px solid #1A1208"
                                boxShadow="3px 3px 0px #1A1208"
                                transition="all 0.15s ease"
                                _hover={{ bg: "gold.300", transform: "translate(-2px, -2px)", boxShadow: "5px 5px 0px #1A1208" }}
                                onClick={() => navigate("/demo")}
                            >
                                Try Interactive Demo <Icon as={FaArrowRight} ml={2} />
                            </Button>
                            <Button
                                size="lg"
                                variant="outline"
                                borderColor="whiteAlpha.400"
                                color="white"
                                px={8}
                                fontWeight="semibold"
                                borderRadius="xl"
                                transition="all 0.15s ease"
                                _hover={{ bg: "whiteAlpha.200", transform: "translateY(-2px)" }}
                                onClick={() => navigate("/pricing")}
                            >
                                View Pricing
                            </Button>
                        </HStack>
                    </VStack>
                </Container>
            </Box>

            {/* Feature grid */}
            <Box py={[16, 24]} className="notebook-dots">
                <Container maxW="7xl">
                    <SimpleGrid columns={[1, 2]} gap={8}>
                        {features.map((feature, i) => (
                            <Box
                                key={feature.title}
                                p={8}
                                borderRadius="2xl"
                                border="2.5px solid #0A3D2E"
                                bg="white"
                                boxShadow="4px 5px 0px #0A3D2E"
                                position="relative"
                                style={{
                                    transform: i % 2 === 0 ? "rotate(-0.5deg)" : "rotate(0.5deg)",
                                }}
                                transition="all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)"
                                _hover={{
                                    transform: "translate(-3px, -3px) rotate(0deg) scale(1.01)",
                                    boxShadow: "6px 8px 0px #0A3D2E",
                                }}
                            >
                                {i % 2 === 0 ? (
                                    <WashiTape angle={-3} top="-12px" left="28px" tapeColor={feature.tapeColor} label={feature.badge} />
                                ) : (
                                    <DoodlePaperclip color="#10B981" size={32} position="absolute" top="-12px" right="24px" />
                                )}

                                <VStack align="start" gap={5} pt={i % 2 === 0 ? 2 : 0}>
                                    <HStack gap={4}>
                                        <Box
                                            p={3}
                                            bg="#DCFCE7"
                                            border="2px solid #0A3D2E"
                                            borderRadius="xl"
                                            color="brand.900"
                                            boxShadow="2px 2px 0px #0A3D2E"
                                        >
                                            <Icon as={feature.icon} fontSize="xl" />
                                        </Box>
                                        <VStack align="start" gap={0}>
                                            <Heading as="h3" fontSize="xl" fontWeight="extrabold" color="gray.900">
                                                {feature.title}
                                            </Heading>
                                        </VStack>
                                    </HStack>

                                    <Text fontSize="sm" color="gray.600" lineHeight="1.8">
                                        {feature.description}
                                    </Text>

                                    <SimpleGrid columns={[1, 2]} gap={2.5} w="full" pt={1}>
                                        {feature.highlights.map((h) => (
                                            <HStack key={h} gap={2} bg="#FAF8F2" p={2} borderRadius="lg" border="1px solid #E2E8F0">
                                                <Icon as={FaCheckCircle} color="brand.700" fontSize="xs" flexShrink={0} />
                                                <Text fontSize="xs" fontWeight="semibold" color="gray.700">{h}</Text>
                                            </HStack>
                                        ))}
                                    </SimpleGrid>
                                </VStack>
                            </Box>
                        ))}
                    </SimpleGrid>
                </Container>
            </Box>

            {/* Bottom CTA banner */}
            <Box py={[16, 20]} bg="brand.900" color="white" borderTop="3px solid" borderColor="gold.500">
                <Container maxW="3xl">
                    <VStack gap={6} textAlign="center">
                        <Heading as="h2" fontSize={["2xl", "3xl", "4xl"]} fontWeight="extrabold">
                            Ready to simplify your school?
                        </Heading>
                        <Text opacity={0.8} fontSize="md" maxW="lg">
                            Small schools with fewer than 50 students can use Result completely free — forever.
                        </Text>
                        <HStack gap={4} flexWrap="wrap" justify="center" pt={2}>
                            <Button
                                size="lg"
                                bg="gold.400"
                                color="#1A1208"
                                fontWeight="bold"
                                px={8}
                                borderRadius="xl"
                                border="2px solid #1A1208"
                                boxShadow="3px 3px 0px #1A1208"
                                transition="all 0.15s ease"
                                _hover={{ bg: "gold.300", transform: "translate(-2px, -2px)", boxShadow: "5px 5px 0px #1A1208" }}
                                onClick={() => (window.location.href = APP_REGISTER_URL)}
                            >
                                Get Started Free
                            </Button>
                            <Button
                                size="lg"
                                variant="outline"
                                borderColor="whiteAlpha.400"
                                color="white"
                                px={8}
                                fontWeight="semibold"
                                borderRadius="xl"
                                transition="all 0.15s ease"
                                _hover={{ bg: "whiteAlpha.200", transform: "translateY(-2px)" }}
                                onClick={() => navigate("/contact")}
                            >
                                Contact Support
                            </Button>
                        </HStack>
                    </VStack>
                </Container>
            </Box>

            <PublicFooter />
        </Box>
    );
};

export default FeaturesPage;
